<template>
  <div class="dashboard-atendimentos">
    <!-- Header do Painel -->
    <div class="dashboard-header mb-4">
      <div class="d-flex align-items-center justify-content-between">
        <div>
          <h2 class="dashboard-title mb-2">
            <feather-icon
              icon="BarChart2Icon"
              size="32"
              class="mr-2"
            />
            Painel de Atendimentos
          </h2>
          <p class="text-muted mb-0">
            Visualize métricas de <strong>CSAT</strong>, tempo de resolução, tags e picos de horário dos atendimentos finalizados
          </p>
        </div>
      </div>
    </div>

    <!-- Se houver mais de uma loja, exibir select -->
    <b-card
      v-if="facebookAccountsLista.length > 1"
      class="mb-4"
    >
      <div class="d-flex align-items-center">
        <feather-icon
          icon="StoreIcon"
          size="18"
          class="mr-2 text-primary"
        />
        <label class="mb-0 mr-3 font-weight-bold">Selecionar Loja:</label>
        <b-form-select
          v-model="chatSelecionado"
          :options="facebookAccountsLista.map(conta => ({
            value: conta.chat_token,
            text: conta.name
          }))"
          style="max-width: 300px;"
        />
      </div>
    </b-card>

    <!-- Filtro por Período -->
    <b-card class="filtros-card mb-4">
      <h5 class="mb-3">
        <feather-icon
          icon="CalendarIcon"
          size="18"
          class="mr-2 text-primary"
        />
        Filtros de Período
      </h5>
      <div class="d-flex flex-wrap align-items-end gap-2">
        <b-input-group>
          <b-form-datepicker
            v-model="periodoInicio"
            class="mr-2"
            locale="pt-BR"
            :date-format-options="{ day: 'numeric', month: 'short', year: 'numeric' }"
            style="width: 160px; font-size: 0.85rem"
          />

          <b-form-datepicker
            v-model="periodoFim"
            class="mr-2"
            locale="pt-BR"
            :date-format-options="{ day: 'numeric', month: 'short', year: 'numeric' }"
            style="width: 160px; font-size: 0.85rem"
          />

          <b-button
            class="mr-2"
            :variant="atalhoSelecionado === '7' ? 'primary' : 'outline-primary'"
            @click="aplicarAtalho(7)"
          >
            Últimos 7 dias
          </b-button>

          <b-button
            class="mr-2"
            :variant="atalhoSelecionado === '30' ? 'primary' : 'outline-primary'"
            @click="aplicarAtalho(30)"
          >
            Últimos 30 dias
          </b-button>

          <b-button
            variant="primary"
            :disabled="carregando"
            class="ml-2"
            @click="carregarDados"
          >
            <span v-if="carregando">
              <b-spinner
                small
                type="grow"
                class="mr-1"
              /> Carregando...
            </span>
            <span v-else>
              Filtrar
            </span>
          </b-button>
        </b-input-group>
      </div>
    </b-card>

      <!-- Gráfico de CSAT com Emojis -->
      <b-card class="csat-card mb-4 text-center">
        <h5 class="card-title-custom mb-4">
          Satisfação dos Clientes (CSAT)
        </h5>
        <div class="d-flex justify-content-around">
          <div
            v-for="nota in [1,2,3,4,5]"
            :key="nota"
            class="text-center"
          >
            <div style="font-size: 2rem">
              {{ nota === 1 ? '😠' : nota === 2 ? '😕' : nota === 3 ? '😐' : nota === 4 ? '😊' : '🤩' }}
            </div>
            <div class="mt-1 font-weight-bold">
              {{ csatResumo[nota - 1] || 0 }}
            </div>
          </div>
        </div>

        <b-button
          v-b-toggle.collapse-csat
          variant="link"
          class="mt-3"
          style="text-decoration: none;"
        >
          <feather-icon
            icon="ChevronDownIcon"
            class="mr-1"
          />
          CSAT Atendentes
        </b-button>

        <b-collapse
          id="collapse-csat"
          class="mt-2"
        >
          <div class="text-left px-3">
            <ul class="list-unstyled mb-0">
              <li
                v-for="(media, nome) in csatMediaPorAtendente"
                :key="nome"
                class="d-flex justify-content-between border-bottom py-1"
              >
                <span>{{ nome }}</span>
                <span class="font-weight-bold">{{ media.toFixed(2) }}</span>
              </li>
            </ul>
          </div>
        </b-collapse>

      </b-card>

      <!-- Gráfico Heatmap - Ocupa a linha inteira -->
      <b-card class="heatmap-card mb-4 text-center">
        <h5 class="card-title-custom mb-4">
          <feather-icon
            icon="ActivityIcon"
            size="20"
            class="mr-2"
          />
          Distribuição por Dia e Hora (Heatmap)
        </h5>
        <vue-apex-chart
          type="heatmap"
          height="400"
          :options="heatmapOptions"
          :series="heatmapSeries"
        />
      </b-card>

      <!-- Gráficos -->
      <div class="row">
        <div
          v-for="(grafico, index) in graficos"
          :key="index"
          class="col-12 col-md-6 mb-4"
        >
          <GraficoApex
            :titulo="grafico.titulo"
            :tipo="grafico.tipo"
            :options="grafico.options"
            :series="grafico.series"
          />
        </div>
      </div>

      <ModalHistoricoAtendimento
        :visivel.sync="modalHistoricoVisivel"
        :historico="atendimentoSelecionado"
      />

      <!-- Tabela de Atendimentos -->
      <b-card class="table-card">
        <h5 class="card-title-custom mb-4">
          <feather-icon
            icon="ListIcon"
            size="20"
            class="mr-2"
          />
          Lista de Atendimentos
        </h5>
        
        <b-card class="filtros-lista-card mb-4">
          <h6 class="filtros-title mb-3">
            <feather-icon
              icon="FilterIcon"
              size="18"
              class="mr-2"
            />
            Filtros da Lista
          </h6>
          <b-form class="d-flex flex-wrap align-items-end">
            <div class="mr-3 mb-2">
              <label class="mb-1 text-muted small">Atendente</label>
              <b-form-select
                v-model="filtro.atendente"
                :options="listaAtendentes"
                size="sm"
              />
            </div>

            <div class="mr-3 mb-2">
              <label class="mb-1 text-muted small">CSAT</label>
              <b-form-select
                v-model="filtro.csat"
                size="sm"
              >
                <option :value="null">
                  Todas
                </option>
                <option
                  v-for="nota in [1,2,3,4,5]"
                  :key="nota"
                  :value="nota"
                >
                  {{ nota }}
                </option>
              </b-form-select>
            </div>

            <div class="mr-3 mb-2">
              <label class="mb-1 text-muted small">Data</label>
              <b-form-datepicker
                v-model="filtro.data"
                size="sm"
                placeholder="Todas"
                :reset-button="true"
                :hide-header="false"
                :clear-button="true"
              />
            </div>

            <div class="mr-3 mb-2">
              <label class="mb-1 text-muted small">Tag</label>
              <b-form-input
                v-model="filtro.tag"
                placeholder="Ex: urgente"
                size="sm"
              />
            </div>

            <div class="mb-2">
              <label class="mb-1 text-muted small">Nome ou WhatsApp</label>
              <b-form-input
                v-model="filtro.nomeOuWhatsapp"
                placeholder="Buscar contato..."
                size="sm"
              />
            </div>

            <b-button
              size="sm"
              class="ml-auto mt-2"
              variant="outline-secondary"
              @click="limparFiltros"
            >
              Limpar Filtros
            </b-button>
          </b-form>
        </b-card>

        <b-table
          :items="listaFiltrada.slice((paginaAtual - 1) * 10, paginaAtual * 10)"
          :fields="camposTabela"
          striped
          hover
          responsive
          small
          bordered
          class="tabela-moderna shadow-sm rounded"
          @row-clicked="abrirHistoricoCliente"
        >
          <template #cell(csat_score)="{ value }">
            <span
              :class="{
                'text-danger font-weight-bold': value <= 2,
                'text-warning font-weight-bold': value === 3,
                'text-success font-weight-bold': value >= 4
              }"
            >
              {{ value || '-' }}
            </span>
          </template>
          <template #cell(tempo_resolucao)="{ value }">
            <span
              :class="{
                'text-success font-weight-bold': value < 10,
                'text-warning font-weight-bold': value >= 10 && value <= 30,
                'text-danger font-weight-bold': value > 30
              }"
            >
              {{ value }} min
            </span>
          </template>
          <template #cell(inicio)="data">
            {{ new Date(data.value).toLocaleString('pt-BR', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            }) }}
          </template>

          <template #cell(fim)="data">
            {{ data.value
              ? new Date(data.value).toLocaleString('pt-BR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })
              : '-' }}
          </template>
          <template #cell(tags)="data">
            <b-badge
              v-for="tag in data.value"
              :key="tag"
              variant="primary"
              class="mr-1"
            >
              {{ tag }}
            </b-badge>
          </template>

        </b-table>
        <b-pagination
          v-model="paginaAtual"
          :total-rows="listaFiltrada.length"
          :per-page="10"
          size="sm"
          hide-goto-end-buttons
          align="center"
        />

      </b-card>
  </div>
</template>

<script>
import VueApexCharts from 'vue-apexcharts'
import {
  buscaResolvidos, getChatOfContact, buscaAtendentes, buscaChatsTeste,
} from '../../functions/basics/chat'
import ModalHistoricoAtendimento from './ModalHistoricoAtendimento'
import GraficoApex from './dashboard/componentes/GraficoApex.vue'

export default {
  components: {
    ModalHistoricoAtendimento,
    GraficoApex,
    'vue-apex-chart': VueApexCharts,
  },
  data() {
    const now = new Date()
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1)
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0)

    return {
      facebookAccountsLista: [], // Lista completa de chats
      chatSelecionado: null, // chat_token atualmente selecionado
      atalhoSelecionado: null,
      atalhoSelecionado: null,
      alteradoManual: false,
      facebookAccounts: null,
      heatmapOptions: {
        chart: { id: 'heatmap', toolbar: { show: false } },
        dataLabels: { enabled: true },
        plotOptions: {
          heatmap: {
            shadeIntensity: 0.5,
            colorScale: {
              ranges: [], // será preenchido dinamicamente
            },
          },
        },
        xaxis: { title: { text: 'Hora' } },
        yaxis: { title: { text: 'Data' } },
      },
      heatmapSeries: [],
      graficoTags: {},
      mapaAtendentes: {},
      csatMediaPorAtendente: {},
      modalHistoricoVisivel: false,
      atendimentoSelecionado: null,
      carregando: false,
      paginaAtual: 1,
      periodoInicio: firstDay,
      periodoFim: lastDay,
      filtro: {
        atendente: null,
        csat: null,
        data: null,
        tag: '',
        nomeOuWhatsapp: '',
      },
      graficos: [],
      listaAtendentes: [],
      atendimentos: [],
      csatResumo: [0, 0, 0, 0, 0],
      camposTabela: [
        { key: 'cliente.name', label: 'Cliente' },
        { key: 'cliente.whatsapp', label: 'WhatsApp' },
        { key: 'csat_score', label: 'CSAT' },
        { key: 'inicio', label: 'Início' },
        { key: 'fim', label: 'Fim' },
        { key: 'tempo_resolucao', label: 'Tempo de Resolução (min)' },
        { key: 'tags', label: 'Tags' },
        { key: 'atendente_nome', label: 'Atendente' },
      ],
    }
  },
  computed: {
    listaFiltrada() {
      const termoBruto = (this.filtro.nomeOuWhatsapp || '').toLowerCase().trim()
      const termoNumerico = termoBruto.replace(/\D/g, '')
      const termoTexto = termoBruto.replace(/\d/g, '')

      return this.atendimentos.filter(at => {
        const nomeCliente = (at.cliente?.name || '').toLowerCase()
        const whatsappCliente = (at.cliente?.whatsapp || '').replace(/\D/g, '')

        const filtroAtendente = !this.filtro.atendente
        || (this.filtro.atendente === 'bot' && !at.atendente_id)
        || at.atendente_id == this.filtro.atendente

        const filtroCSAT = !this.filtro.csat || at.csat_score == this.filtro.csat
        const filtroData = !this.filtro.data || new Date(at.inicio).toDateString() === new Date(this.filtro.data).toDateString()
        const filtroTag = !this.filtro.tag || (at.tags || []).includes(this.filtro.tag.toLowerCase())

        const filtroBusca = !termoBruto
          || (termoTexto && nomeCliente.includes(termoTexto))
          || (termoNumerico && whatsappCliente.includes(termoNumerico))

        return filtroAtendente && filtroCSAT && filtroData && filtroTag && filtroBusca
      })
    },

  },
  watch: {
    periodoInicio(newVal, oldVal) {
      if (!this.alteradoManual) {
        this.alteradoManual = true
        this.atalhoSelecionado = null
      }
    },
    periodoFim(newVal, oldVal) {
      if (!this.alteradoManual) {
        this.alteradoManual = true
        this.atalhoSelecionado = null
      }
    },
  },
  mounted() {
    this.facebookCredentials().then(() => {
      this.carregarDados()
    })
  },
  methods: {
    limparFiltros() {
      this.filtro = {
        atendente: null,
        csat: null,
        data: null,
        tag: '',
        nomeOuWhatsapp: '',
      }
    },
    aplicarAtalho(dias) {
      const hoje = new Date()
      const inicio = new Date()
      inicio.setDate(hoje.getDate() - dias + 1)
      this.periodoInicio = inicio
      this.periodoFim = hoje

      // Zera flag de alteração manual
      this.alteradoManual = false

      // Marca atalho como selecionado
      this.atalhoSelecionado = dias
    },
    formatarData(date) {
      if (!date) return ''
      const opcoes = { day: '2-digit', month: '2-digit', year: 'numeric' }
      return new Date(date).toLocaleDateString('pt-BR', opcoes) // exemplo: 01/07/2025
    },
    async facebookCredentials() {
      const contas = await buscaChatsTeste()
      this.facebookAccountsLista = contas.chats || []

      if (contas.chats.length === 1) {
        this.chatSelecionado = contas.chats[0].chat_token
      } else if (contas.chats.length > 1) {
        this.chatSelecionado = contas.chats[0].chat_token // Seleciona a primeira por padrão
      } else {
        this.$bvToast.toast('Nenhuma loja encontrada.', {
          title: 'Erro',
          variant: 'danger',
          solid: true,
        })
      }
    },
    async abrirHistoricoCliente(atendimento) {
      const dados = await getChatOfContact(atendimento.atendimento_id)
      this.atendimentoSelecionado = { mensagens: dados }
      this.modalHistoricoVisivel = true
    },

    async carregarDados() {
      if (!this.periodoInicio || !this.periodoFim) {
        this.$bvToast.toast('Por favor, selecione um período válido.', {
          title: 'Atenção',
          variant: 'warning',
          solid: true,
        })
        return
      }

      const inicio = new Date(this.periodoInicio)
      const fim = new Date(this.periodoFim)

      if (inicio > fim) {
        this.$bvToast.toast('A data de início não pode ser maior que a data de fim.', {
          title: 'Período inválido',
          variant: 'danger',
          solid: true,
        })
        return
      }

      const umMesEmMs = 1000 * 60 * 60 * 24 * 31
      if ((fim - inicio) > umMesEmMs) {
        this.$bvToast.toast('O período não pode exceder 31 dias.', {
          title: 'Período muito longo',
          variant: 'warning',
          solid: true,
        })
        return
      }

      this.carregando = true
      const uuid = this.chatSelecionado
      const listaAtendentesAPI = await buscaAtendentes(uuid)

      this.mapaAtendentes = {}
      listaAtendentesAPI.atendentes.forEach(a => {
        this.mapaAtendentes[a.id] = a.name
      })

      this.listaAtendentes = [
        { value: null, text: 'Todos' },
        { value: 'bot', text: 'Bot' },
        ...listaAtendentesAPI.atendentes.map(({ id, name }) => ({
          value: id,
          text: name,
        })),
      ]

      const inicioISO = new Date(this.periodoInicio).toISOString().split('T')[0]
      const fimISO = new Date(this.periodoFim).toISOString().split('T')[0]
      const dados = await buscaResolvidos(uuid, inicioISO, fimISO, 'all')

      this.csatResumo = [0, 0, 0, 0, 0]
      const csatMediaPorAtendente = {}
      const csatContagem = {}

      this.atendimentos = dados.resolvidos.map(a => {
        const inicio = new Date(a.inicio)
        const fimBruto = a.fim || a.ultima_mensagem_em || a.ultima_mensagem?.timestamp || a.inicio
        const fim = new Date(fimBruto)
        const tempo_resolucao = Math.round((fim - inicio) / 1000 / 60)

        if (a.csat_score >= 1 && a.csat_score <= 5) {
          this.csatResumo[a.csat_score - 1]++
          const nome = this.mapaAtendentes[a.atendente_ativo?.id] || 'Bot'
          csatMediaPorAtendente[nome] = (csatMediaPorAtendente[nome] || 0) + a.csat_score
          csatContagem[nome] = (csatContagem[nome] || 0) + 1
        }

        return {
          ...a,
          atendente_id: a.atendente_ativo?.id || null,
          atendente_nome: a.atendimento_bot
            ? 'Bot'
            : a.atendente_ativo?.id
              ? this.mapaAtendentes[a.atendente_ativo.id] || 'Desconhecido'
              : 'Desconhecido',
          csat_score: a.csat_score || null,
          tags: a.tags || [],
          tempo_resolucao,
        }
      })

      // Calcula médias de CSAT por atendente
      Object.keys(csatMediaPorAtendente).forEach(nome => {
        csatMediaPorAtendente[nome] = csatMediaPorAtendente[nome] / csatContagem[nome]
      })
      this.csatMediaPorAtendente = csatMediaPorAtendente

      // Gráficos auxiliares
      const porAtendente = {}
      const picosHora = {}
      const porCliente = {}
      const mapaTags = {}
      let totalMinutos = 0
      let atendimentosComTempo = 0
      let humanos = 0
      let bots = 0
      let desconhecidos = 0

      this.atendimentos.forEach(at => {
        const inicio = new Date(at.inicio)
        const fim = new Date(at.fim || at.ultima_mensagem?.timestamp || at.inicio)
        const duracaoMin = (fim - inicio) / 1000 / 60

        if (!isNaN(duracaoMin)) {
          totalMinutos += duracaoMin
          atendimentosComTempo++
        }

        const hora = inicio.getHours()
        picosHora[hora] = (picosHora[hora] || 0) + 1

        if (at.atendimento_bot === true) {
          bots++
        } else if (at.atendente_ativo && at.atendente_ativo.id) {
          humanos++
        } else {
          desconhecidos++
        }

        const nome = at.atendente_nome
        porAtendente[nome] = (porAtendente[nome] || 0) + 1

        const clienteNome = at.cliente?.name || 'Desconhecido'
        porCliente[clienteNome] = (porCliente[clienteNome] || 0) + 1;

        (at.tags || []).forEach(tag => {
          mapaTags[tag] = (mapaTags[tag] || 0) + 1
        })
      })

      const categoriasHoras = Object.keys(picosHora).sort((a, b) => a - b)

      // Gráfico de Tags separado (ao lado do heatmap)
      this.graficoTags = {
        titulo: 'Distribuição por Tags',
        tipo: 'bar',
        options: {
          chart: { id: 'tags' },
          xaxis: { categories: Object.keys(mapaTags) },
        },
        series: [{ name: 'Atendimentos', data: Object.values(mapaTags) }],
      }

      // Gráficos principais
      this.graficos = [
        {
          titulo: 'Top Clientes mais Atendidos',
          tipo: 'bar',
          options: {
            chart: { id: 'topClientes' },
            xaxis: { categories: Object.keys(porCliente).slice(0, 10) },
          },
          series: [{ name: 'Atendimentos', data: Object.values(porCliente).slice(0, 10) }],
        },
        {
          titulo: 'Tempo Médio de Resolução',
          tipo: 'line',
          options: { chart: { id: 'tempoMedio' }, xaxis: { categories: ['Média'] } },
          series: [{ name: 'Tempo Médio (min)', data: [Math.round(totalMinutos / Math.max(atendimentosComTempo, 1))] }],
        },
        {
          titulo: 'Atendimentos por Atendente',
          tipo: 'bar',
          options: { chart: { id: 'porAtendente' }, xaxis: { categories: Object.keys(porAtendente) } },
          series: [{ name: 'Atendimentos', data: Object.values(porAtendente) }],
        },
        {
          titulo: 'Bot vs Humano',
          tipo: 'pie',
          options: { labels: ['Humano', 'Bot', 'Desconhecido'] },
          series: [humanos, bots, desconhecidos],
        },
        {
          titulo: 'Pico de Atendimentos',
          tipo: 'line',
          options: {
            chart: { id: 'picos' },
            xaxis: { categories: categoriasHoras.map(h => `${h.toString().padStart(2, '0')}h`) },
          },
          series: [{ name: 'Atendimentos', data: categoriasHoras.map(h => picosHora[h]) }],
        },
        this.graficoTags,
      ]

      // Heatmap de Atendimentos por Dia e Hora
      const mapaHeatmap = {}
      const diasUnicos = new Set()
      this.atendimentos.forEach(at => {
        const data = new Date(at.inicio)
        const dia = data.toLocaleDateString('pt-BR')
        const hora = `${data.getHours().toString().padStart(2, '0')}h`
        diasUnicos.add(dia)

        if (!mapaHeatmap[dia]) mapaHeatmap[dia] = {}
        mapaHeatmap[dia][hora] = (mapaHeatmap[dia][hora] || 0) + 1
      })

      this.heatmapSeries = [...diasUnicos].map(dia => {
        const horas = Array.from({ length: 24 }, (_, i) => `${i.toString().padStart(2, '0')}h`)
        return {
          name: dia,
          data: horas.map(h => ({
            x: h,
            y: mapaHeatmap[dia]?.[h] || 0,
          })),
        }
      })

      // Faixas dinâmicas com tons de roxo
      let maxValor = 0
      this.heatmapSeries.forEach(dia => {
        dia.data.forEach(ponto => {
          if (ponto.y > maxValor) maxValor = ponto.y
        })
      })

      const faixa1 = Math.floor(maxValor * 0.25)
      const faixa2 = Math.floor(maxValor * 0.5)
      const faixa3 = Math.floor(maxValor * 0.75)

      this.heatmapOptions = {
        chart: { id: 'heatmap', toolbar: { show: false } },
        dataLabels: {
          enabled: true,
          style: {
            colors: ['#ffffff'],
            fontSize: '14px',
            fontWeight: 'bold',
          },
        },
        plotOptions: {
          heatmap: {
            shadeIntensity: 0.3,
            useFillColorAsStroke: false,
            colorScale: {
              ranges: [
                {
                  from: 0, to: faixa1, color: '#ede7f6', name: 'Baixo',
                },
                {
                  from: faixa1 + 1, to: faixa2, color: '#ce93d8', name: 'Médio',
                },
                {
                  from: faixa2 + 1, to: faixa3, color: '#ab47bc', name: 'Alto',
                },
                {
                  from: faixa3 + 1, to: maxValor, color: '#6a1b9a', name: 'Muito alto',
                },
              ],
            },
          },
        },
        xaxis: {
          title: { text: 'Hora' },
          labels: { style: { fontSize: '12px' } },
        },
        yaxis: {
          title: { text: 'Data' },
          labels: { style: { fontSize: '12px' } },
        },
        stroke: { width: 1, colors: ['#ffffff'] },
        tooltip: {
          enabled: true,
          y: {
            formatter: val => `${val} atendimentos`,
          },
        },
      }

      this.$nextTick(() => {
        const el = this.$el
        if (el?.scrollIntoView) {
          el.scrollIntoView({ behavior: 'smooth' })
        }
      })

      this.carregando = false
    },

  },
}
</script>

<style>
/* Estilos aplicados APENAS na página de dashboard */
body:has(.dashboard-atendimentos) .content-area-wrapper {
  overflow-y: auto !important;
  overflow-x: hidden !important;
}

body:has(.dashboard-atendimentos) .app-content.content {
  padding-left: 0 !important;
  padding-right: 0 !important;
}

body:has(.dashboard-atendimentos) .content-wrapper {
  padding: 0 !important;
  margin: 0 !important;
}

body:has(.dashboard-atendimentos) .content-body {
  padding: 0 !important;
  margin: 0 !important;
}

body:has(.dashboard-atendimentos) .content-right {
  width: 100% !important;
}

/* Mobile */
@media (max-width: 768px) {
  body:has(.dashboard-atendimentos) .app-content.content {
    padding: 0 !important;
  }
}
</style>

<style scoped>
/* Dashboard Container */
.dashboard-atendimentos {
  padding: 2rem;
  min-height: 100vh;
}

/* Header */
.dashboard-header {
  background: linear-gradient(135deg, #9A3092 0%, #c44ab8 100%);
  padding: 2.5rem;
  border-radius: 16px;
  box-shadow: 0 8px 24px rgba(154, 48, 146, 0.25);
  position: relative;
  overflow: hidden;
}

.dashboard-title {
  font-size: 2rem;
  font-weight: 700;
  color: #ffffff;
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
  position: relative;
  z-index: 1;
}

.dashboard-title .feather-icon {
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2));
}

.dashboard-header p {
  color: rgba(255, 255, 255, 0.95);
  font-size: 1rem;
  position: relative;
  z-index: 1;
}

/* Cards Gerais */
.dashboard-atendimentos .card {
  border-radius: 16px;
  border: none;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  background: #ffffff;
}

.dashboard-atendimentos .card:hover {
  box-shadow: 0 8px 24px rgba(154, 48, 146, 0.15);
}

/* Títulos dos Cards */
.card-title-custom {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #5e5873;
  font-weight: 600;
  font-size: 1.25rem;
}

.card-title-custom .feather-icon {
  color: #9A3092;
}

/* Filtros Card */
.filtros-card {
  background: linear-gradient(135deg, rgba(154, 48, 146, 0.05) 0%, #ffffff 100%);
  border: 2px solid rgba(154, 48, 146, 0.1);
}

.filtros-card h5 {
  display: flex;
  align-items: center;
  color: #5e5873;
  font-weight: 600;
}

.filtros-card h5 .feather-icon {
  color: #9A3092;
}

/* CSAT Card */
.csat-card {
  background: linear-gradient(135deg, #ffffff 0%, rgba(154, 48, 146, 0.03) 100%);
  border: 2px solid rgba(154, 48, 146, 0.1);
}

/* Heatmap Card */
.heatmap-card {
  background: #ffffff;
  border: 2px solid rgba(154, 48, 146, 0.1);
}

/* Table Card */
.table-card {
  background: #ffffff;
  border: 2px solid rgba(154, 48, 146, 0.1);
}

/* Filtros Lista Card */
.filtros-lista-card {
  background: linear-gradient(135deg, rgba(154, 48, 146, 0.03) 0%, #ffffff 100%);
  border: 1px solid rgba(154, 48, 146, 0.1);
}

.filtros-title {
  display: flex;
  align-items: center;
  color: #5e5873;
  font-weight: 600;
}

.filtros-title .feather-icon {
  color: #9A3092;
}

/* Gap utility (compatibilidade) */
.gap-2 > * {
  margin-right: 0.5rem;
  margin-bottom: 0.5rem;
}

.gap-2 > *:last-child {
  margin-right: 0;
}

/* Table */
.table thead th {
  position: sticky;
  top: 0;
  background-color: #fff;
  z-index: 5;
}

.tabela-moderna {
  border-radius: 12px;
  overflow: hidden;
  font-size: 0.85rem;
  background-color: #fff;
  transition: all 0.2s ease;
}

.tabela-moderna thead th {
  background-color: #f8f9fa;
  font-weight: 600;
  font-size: 0.78rem;
  text-transform: uppercase;
  color: #6c757d;
  letter-spacing: 0.5px;
}

.tabela-moderna tbody tr {
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.tabela-moderna tbody tr:hover {
  background-color: #f1f3f5;
}

.tabela-moderna td {
  vertical-align: middle;
  padding: 0.55rem 0.75rem;
}

/* Botões Customizados */
.dashboard-atendimentos .btn-primary {
  background: linear-gradient(135deg, #9A3092 0%, #c44ab8 100%);
  border: none;
  box-shadow: 0 4px 12px rgba(154, 48, 146, 0.3);
  transition: all 0.3s ease;
}

.dashboard-atendimentos .btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(154, 48, 146, 0.4);
}

.dashboard-atendimentos .btn-outline-primary {
  border: 2px solid #9A3092;
  color: #9A3092;
  background: transparent;
  transition: all 0.3s ease;
}

.dashboard-atendimentos .btn-outline-primary:hover {
  background: linear-gradient(135deg, #9A3092 0%, #c44ab8 100%);
  border-color: #9A3092;
  color: white;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(154, 48, 146, 0.3);
}

/* Emojis CSAT */
.csat-card .d-flex > div {
  transition: all 0.3s ease;
  padding: 1rem;
  border-radius: 12px;
}

.csat-card .d-flex > div:hover {
  background: rgba(154, 48, 146, 0.05);
  transform: scale(1.1);
}

/* Badges */
.badge-primary {
  background: linear-gradient(135deg, #9A3092 0%, #c44ab8 100%);
  box-shadow: 0 2px 8px rgba(154, 48, 146, 0.3);
}

/* Paginação */
.dashboard-atendimentos .pagination .page-item.active .page-link {
  background: linear-gradient(135deg, #9A3092 0%, #c44ab8 100%);
  border-color: #9A3092;
  box-shadow: 0 2px 8px rgba(154, 48, 146, 0.3);
}

/* Responsividade */
@media (max-width: 768px) {
  .dashboard-atendimentos {
    padding: 1rem;
  }

  .dashboard-header {
    padding: 1.5rem;
  }

  .dashboard-title {
    font-size: 1.5rem;
  }

  .card-title-custom {
    font-size: 1.1rem;
  }

  .gap-2 {
    flex-direction: column;
  }

  .gap-2 > * {
    width: 100%;
    margin-right: 0;
  }
}
</style>
